import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

// Validate environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing required Supabase environment variables');
}

// Create client with enhanced configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'serialsuite_auth',
    storage: window.localStorage
  },
  global: {
    headers: { 'x-application-name': 'serialsuite' }
  },
  db: {
    schema: 'public'
  }
});

// Enhanced connection check with retry logic
export const checkSupabaseConnection = async (retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      // First check if user is authenticated
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      
      if (authError) {
        // If session error, try to refresh
        const { data: { session: refreshedSession }, error: refreshError } = 
          await supabase.auth.refreshSession();
          
        if (refreshError || !refreshedSession) {
          // If refresh fails, redirect to login
          window.location.href = '/';
          return { ok: false, error: new Error('Authentication required') };
        }
      }

      if (!session) {
        window.location.href = '/';
        return { ok: false, error: new Error('Not authenticated') };
      }

      // Then check database connection
      const { data, error: dbError } = await supabase
        .from('companies')
        .select('id')
        .eq('id', session.user.id)
        .single();

      if (dbError) {
        // Only retry for connection errors, not 404s
        if (!dbError.message.includes('PGRST116')) {
          console.warn(`Supabase connection attempt ${i + 1} failed:`, dbError);
          if (i < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
        }
        return { ok: false, error: dbError };
      }

      return { ok: true, data };

    } catch (err) {
      console.error('Unexpected error:', err);
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      return { 
        ok: false, 
        error: err instanceof Error ? err : new Error('Failed to connect to Supabase')
      };
    }
  }

  return {
    ok: false,
    error: new Error('Failed to connect after multiple attempts')
  };
};

// Set up auth state change listener
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT' || !session) {
    // Clear any cached data
    localStorage.removeItem('serialsuite_auth');
    
    // Only redirect if not already on login page
    if (!window.location.pathname.match(/^\/(login|register)?$/)) {
      window.location.href = '/';
    }
  }
});